import {
  AdPreviewCard as ClaireAdPreviewCard,
  OfferPreviewCard,
  asPreviewCardPayload,
} from '@/features/claire/chat-preview';
import { ClipListEditor } from '@/features/content-batches/components/clip-list-editor';
import type { GraphicLogoPlacement } from '@borradh-workspace/contracts';
import type { VideoFootageSelection } from '@borradh-workspace/contracts/claire';
import type { ConfirmationSelection } from '@borradh-workspace/contracts/claire';
import { videoFootageRequiredSchema } from '@borradh-workspace/contracts/claire';
import type { CardEnvelope } from '@borradh-workspace/features/assistant';
import type { MemorySuggestionAnswer } from '../lib/memory-suggestion-replies';
import { packageLaunchErrorMessage } from '../lib/package-launch-error-message';
import { hasToolResultFailure } from '../lib/tool-parts';
import { ArtifactRowSkeleton } from './artifact-row';
import { AdArtifactRow } from './rich/ad-artifact-row';
import { AdPreviewCard, type AdPreviewProps } from './rich/ad-preview-card';
import { ApprovalNotRunNotice } from './rich/approval-not-run-notice';
import { buildAssetPickerSelectionMessage } from './rich/asset-picker-selection-message';
import { AssetReferenceCard } from './rich/asset-reference-card';
import { CampaignPackageAdPreviews } from './rich/campaign-package-review';
import {
  CampaignPreviewCard,
  type CampaignPreviewData,
} from './rich/campaign-preview-card';
import { ChatbotToggleCard } from './rich/chatbot-toggle-card';
import { ClipSelectionGrid } from './rich/clip-selection-grid';
import {
  ConfirmationRequiredCard,
  type ConfirmationRequiredPresentation,
} from './rich/confirmation-required-card';
import { CreatedCard, SavedCampaignCreatedCard } from './rich/created-card';
import { CreatingStatus } from './rich/creating-status';
import { DraftAdLaunchDecision } from './rich/draft-ad-launch-decision';
import { DraftReplyCard } from './rich/draft-reply-card';
import {
  type ExistingCandidate,
  ExistingCandidatesCard,
} from './rich/existing-candidates-card';
import { GraphicDraftPreviewCard } from './rich/graphic-draft-preview';
import { GraphicStatusCard } from './rich/graphic-status-card';
import {
  MemoryListCard,
  type MemoryListPresentation,
  MemoryRemovedCard,
  type MemoryRemovedPresentation,
  MemorySavedCard,
  type MemorySavedPresentation,
  MemoryUpdatedCard,
  type MemoryUpdatedPresentation,
} from './rich/memory-result-cards';
import { MemorySuggestionCard } from './rich/memory-suggestion-card';
import { ProcessingStatus } from './rich/processing-status';
import { QRCodeCard } from './rich/qr-code-card';
import { StopAndAskCard } from './rich/stop-and-ask-card';
import { SupportChatCard } from './rich/support-chat-card';
import { isCreatedState, isCreatingState } from './rich/types';
import { VideoDraftPreviewCard } from './rich/video-draft-preview';
import { VideoFootageRequiredCard } from './rich/video-footage-required-card';
import { WebsiteDesignerApprovalCard } from './rich/website-designer-approval-card';
import { type ToolPartData, toolNameToLabel } from './tool-parts';

export interface ToolRendererProps {
  toolName: string;
  toolPart: ToolPartData;
  /**
   * How a card answers.
   *
   * There is no `onToolRespond` any more. Cards used to answer by writing a
   * tool output back with `addToolOutput({ output: 'approved' })`, which only
   * works while a tool is PAUSED awaiting the browser — and no tool here is.
   * Every one executes on the server and streams its result, so those cards
   * were comparing an object to the string 'approved' and rendering a refusal.
   * An answer is a message; the model reads it and calls the tool again with
   * the confirmation token it is holding.
   */
  onSendMessage: (
    text: string,
    selection?: ConfirmationSelection,
    footageSelection?: VideoFootageSelection
  ) => void;
  /**
   * This card's graphic artifact is no longer the newest one sharing its id
   * (see `getStaleGraphicArtifactToolCallIds`) — several turns can legitimately
   * stage edits on the SAME still-unrendered graphic id, and only the latest
   * of those should ever look "selected" in the transcript. The card itself
   * stays fully visible; only its `ArtifactRow` highlight is suppressed.
   */
  suppressArtifactHighlight?: boolean;
  /**
   * A `memory_suggestion` card's answer, read from a later message in the
   * transcript (see `getMemorySuggestionAnswers`).
   */
  memorySuggestionAnswer?: MemorySuggestionAnswer;
  /** A graphic proposal was answered; its "hit Accept" line is out of date. See `GraphicDraftPreviewCard`. */
  onReplyStale?: (toolCallId: string) => void;
  /**
   * Whether the turn that owns this part is still streaming.
   *
   * A tool call with no output yet is one of two things: still running, or
   * abandoned — the owner pressed stop, or the tool never executed. Both
   * leave the part at `input-available` forever; only the stream tells them
   * apart. The in-flight skeleton below is shown for the first and never for
   * the second, so a cancelled generation cannot leave a permanent skeleton.
   */
  isStreaming?: boolean;
}

/**
 * Tools whose result is an ad artifact, with what to say while they run.
 *
 * Keyed by canonical name. The label is the skeleton's only text, so it is
 * the verb the owner asked for, not the tool's name.
 */
const AD_ARTIFACT_TOOLS: Record<string, string> = {
  meta_ads_createDraftAd: 'Creating ad…',
  meta_ads_replaceAdCreative: 'Updating creative…',
  meta_ads_updateAd: 'Updating ad…',
  meta_ads_executeLaunchAd: 'Launching ad…',
};

/**
 * Maps a tool-part (name + state) to its rich-content renderer. Returns
 * null when no rich UI is appropriate (silent tools, errors handled by
 * the chain-of-thought trace, unknown tools).
 *
 * Rich-content renderer prop shapes are preserved verbatim from v2 — they
 * are exercised against the legacy backend until C-02 swaps it out.
 */
function ToolResultContent({
  toolName,
  toolPart,
  onSendMessage,
  suppressArtifactHighlight,
  memorySuggestionAnswer,
  onReplyStale,
  isStreaming = false,
}: ToolRendererProps) {
  const { state, input, output } = toolPart;
  // `toolName` here is the tool's exact registered name — `getToolName`
  // strips only the `tool-` part-type prefix, so every check below uses the
  // canonical `{feature}_{action}` name (except the legacy content-tools
  // shim's tools, which carry no canonical prefix at all).

  // An ad on its way. The row it becomes has a fixed footprint, so the
  // transcript shows that footprint, pulsing, from the moment the tool is
  // called rather than nothing until it answers. Gated on `isStreaming`: a
  // call parked without output after the stream ends is finished (in
  // failure) and the trace already says so — see `describeToolStep`.
  // Not for a campaign-package draft: that call saves the ad without a card
  // (the package review shows the ads together later), so a skeleton here
  // would pulse and then vanish into nothing.
  const inFlightAdLabel = AD_ARTIFACT_TOOLS[toolName];
  const isPackageDraft =
    (input as { approvalMode?: string } | undefined)?.approvalMode ===
    'campaign_package';
  if (
    inFlightAdLabel &&
    isStreaming &&
    !isPackageDraft &&
    (state === 'input-streaming' || state === 'input-available')
  ) {
    return (
      <ArtifactRowSkeleton
        label={inFlightAdLabel}
        className="w-full sm:max-w-sm"
      />
    );
  }

  // `meta_ads_previewCampaign` still opts out by peeking at its own input: the
  // create-campaign flow reads its signals silently and shows only the
  // createCampaign card. Content tools no longer need this — `createContent`
  // returns `{ type: 'none' }` when the card is suppressed, which is the tool
  // saying so rather than the renderer inferring it from the far side of the
  // wire.
  if (
    toolName === 'meta_ads_previewCampaign' &&
    (input as { suppressCard?: boolean } | undefined)?.suppressCard
  ) {
    return null;
  }

  // Extract the envelope ONCE for the whole switch. `presentation` is typed as
  // `PresentationPayload` on the API side, which still carries a
  // `Record<string, unknown> & { type: string }` escape hatch (Phase 3 removes
  // it). So we treat it as a loose bag here and let each case narrow.
  const presentation = (
    output as
      | { presentation?: { type?: string } & Record<string, unknown> }
      | undefined
  )?.presentation;
  const rawType =
    presentation && typeof presentation === 'object'
      ? (presentation as { type?: unknown }).type
      : undefined;
  const envelope =
    presentation && typeof rawType === 'string'
      ? (presentation as { type: string } & Record<string, unknown>)
      : null;

  // The tool's explicit "render nothing" sentinel. Not a `CardEnvelope` kind —
  // it exists to let a tool suppress its own card (e.g. an offer video created
  // only as the creative for a draft ad, whose combined card shows later in
  // the turn).
  if (
    (!envelope || envelope.type === 'none') &&
    hasToolResultFailure(toolPart)
  ) {
    if (
      toolName === 'content_createContent' &&
      output?.code === 'VIDEO_CREATION_BLOCKED' &&
      typeof output.error === 'string' &&
      /no confirmed uploaded clips or matching stock footage|This template needs [\d.]+ seconds of usable footage/.test(
        output.error
      )
    ) {
      const parsed = videoFootageRequiredSchema.safeParse({
        type: 'video_footage_required',
        message: output.error,
        request: input,
      });
      if (parsed.success)
        return (
          <VideoFootageRequiredCard
            id={toolPart.toolCallId}
            presentation={parsed.data}
            onSendMessage={onSendMessage}
          />
        );
    }
    const recovery = output?.recovery;
    if (
      toolName === 'offers_createOffer' &&
      output?.code === 'OFFER_PRICE_CURRENCY_MISMATCH' &&
      recovery &&
      typeof recovery === 'object' &&
      'requiresUserInput' in recovery &&
      recovery.requiresUserInput === true &&
      'requestedCurrency' in recovery &&
      typeof recovery.requestedCurrency === 'string' &&
      /^[A-Z]{3}$/.test(recovery.requestedCurrency) &&
      'offerCurrency' in recovery &&
      typeof recovery.offerCurrency === 'string' &&
      /^[A-Z]{3}$/.test(recovery.offerCurrency)
    ) {
      return (
        <p role="status" className="text-sm text-muted-foreground">
          This offer uses {recovery.offerCurrency}, but you requested{' '}
          {recovery.requestedCurrency}. What price should I use in{' '}
          {recovery.offerCurrency}?
        </p>
      );
    }
    // Nothing went wrong: the pre-flight wants something only the owner can give
    // (a website, a payment method), and Claire's reply asks for it. Past tense,
    // because this message stays in the transcript after the retry succeeds. A
    // retryable blocker is a lookup that failed, so it stays an error below.
    if (
      toolName === 'meta_ads_createCampaign' &&
      output?.errorCode === 'campaign_prerequisites_blocked' &&
      output.retryable !== true
    ) {
      return (
        <p role="status" className="text-sm text-muted-foreground">
          The campaign needed something from you before it could be created.
        </p>
      );
    }
    return (
      <p role="status" className="text-destructive text-sm">
        {toolName === 'meta_ads_launchCampaignPackage' &&
        !input?.confirmationToken
          ? packageLaunchErrorMessage(output?.code)
          : `${toolNameToLabel(toolName)} could not be completed.`}
      </p>
    );
  }
  if (envelope?.type === 'none') return null;

  // ─────────────────────────────────────────────────────────────────────
  // ONE exhaustive switch over `CardEnvelope['type']`.
  //
  // Every declared kind gets a `case`. Kinds we DON'T currently render in
  // the app (hard_block_violation, and — for now —
  // existing_candidates / preview_card, which are dispatched via `uiState`
  // and `asPreviewCardPayload` further down) break out and fall through to
  // the generic-dispatch path below.
  //
  // The `default:` clause is a Phase 3 tripwire: `const _exhaustive: never
  // = kind` will fail TypeScript the day a new envelope kind is added to
  // `card-envelope.ts` without a matching case here. At runtime it is
  // permissive — `PresentationPayload` still carries the
  // `Record<string, unknown> & { type: string }` escape hatch, so a tool
  // MAY emit an unrecognised `type`; we simply fall through to the generic
  // path rather than crashing.
  // ─────────────────────────────────────────────────────────────────────
  if (envelope) {
    const kind = envelope.type as CardEnvelope['type'];
    switch (kind) {
      case 'video_footage_required': {
        const parsed = videoFootageRequiredSchema.safeParse(envelope);
        if (!parsed.success) break;
        return (
          <VideoFootageRequiredCard
            id={toolPart.toolCallId}
            presentation={parsed.data}
            onSendMessage={onSendMessage}
          />
        );
      }
      case 'content_clips': {
        // The clip list editor: the ordered list, with the approval that
        // renders it attached to the bottom of the same card.
        //
        // The card addresses a CONTENT ITEM, which is the correction five
        // earlier attempts needed. An approved edit forks the video, and the
        // item is what follows the fork — address the video instead and the
        // fork's id ends up somewhere only the browser can see, so "render it
        // now" finds the cut the owner already replaced and correctly reports
        // nothing to do.
        const card = envelope as unknown as {
          itemId?: string;
          attemptId?: string;
          renderCount?: number;
          title?: string;
          serviceId?: string | null;
          minClipCount?: number;
          // #978 (video authority) — added on main against the pre-envelope
          // renderer; carried onto the envelope card so the clip editor can
          // still open on the right pane and show the proposed script.
          openEditor?: 'clips' | 'script';
          scriptText?: string;
          authorityState?:
            | 'draft'
            | 'awaiting_recording'
            | 'awaiting_voice'
            | 'ready_to_render'
            | 'rendering'
            | 'rendered'
            | null;
        };
        if (card.itemId) {
          return (
            <div data-testid="claire-card-content-clips">
              <ClipListEditor
                gateId={toolPart.toolCallId}
                followLatest={
                  toolName === 'content_patchContent' ||
                  (output as { awaitingApproval?: boolean } | undefined)
                    ?.awaitingApproval === true
                }
                itemId={card.itemId}
                // The cut this card was emitted against. Without it every card
                // for an item is equally live, so an older one offers to
                // approve an edit staged long after it was written.
                attemptId={card.attemptId}
                renderCount={card.renderCount}
                title={card.title}
                serviceId={card.serviceId ?? null}
                minClipCount={card.minClipCount ?? 1}
                onCancelled={() => onSendMessage('Cancel this video draft.')}
                onDiscarded={() =>
                  onSendMessage(
                    'The staged edits were discarded. Keep the existing video unchanged.'
                  )
                }
                openEditor={card.openEditor}
                authorityState={card.authorityState}
                proposedScript={card.scriptText}
              />
            </div>
          );
        }
        break;
      }

      case 'video_status': {
        // `video_status` is a render already accepted — nothing left to
        // approve, so the self-polling card. `ProcessingStatus` polls
        // /videos/:id internally, so the missing fields (title, progress,
        // thumbnail) populate on the first poll tick — the loading card
        // appears immediately on Approve without relying on Claire to also
        // call `getVideoStatus`.
        const card = envelope as {
          videoId?: string;
          itemId?: string;
          status?: string;
          title?: string;
        };
        if (card.videoId) {
          return (
            <ProcessingStatus
              data={{
                videoId: card.videoId,
                itemId: card.itemId,
                status: card.status ?? 'queued',
                title: card.title,
              }}
              onPrompt={onSendMessage}
            />
          );
        }
        break;
      }

      case 'video_draft': {
        // `video_draft` is the DEGRADED path: the draft exists but its content
        // item could not be opened, so there is no item to address and the
        // older preview card stands in rather than the draft being lost.
        const card = envelope as {
          videoId?: string;
          title?: string;
          fields?: { label: string; value: string }[];
          serviceId?: string | null;
          clipAssetIds?: string[];
          minClipCount?: number;
          textFrames?: Array<{
            id: string;
            text: string;
            style?: 'default' | 'question' | 'answer' | 'disclaimer' | 'cta';
          }>;
        };
        if (card.videoId) {
          return (
            <VideoDraftPreviewCard
              id={toolPart.toolCallId}
              videoId={card.videoId}
              title={card.title ?? 'Video draft'}
              fields={card.fields ?? []}
              serviceId={card.serviceId ?? null}
              initialClipAssetIds={card.clipAssetIds ?? []}
              minClipCount={card.minClipCount ?? 1}
              textFrames={card.textFrames}
              onSendMessage={onSendMessage}
            />
          );
        }
        break;
      }

      case 'graphic_draft': {
        // `graphic_draft` is a PROPOSAL — nothing has been generated yet, and
        // the card owns source-image selection so the owner picks before the
        // render is spent.
        const card = envelope as {
          itemId?: string;
          attemptId?: string;
          serviceId?: string;
          offerId?: string;
          category?: string;
          imageMode?: 'generate' | 'assemble';
          kind?: string;
          style?: string;
          slideCount?: number;
          candidateAssetIds?: string[];
          sourceAssetIds?: string[];
          topicSummary?: string;
          refinementInstruction?: string;
          includeLogo?: boolean;
          logoPlacement?: GraphicLogoPlacement;
          imageryPolicy?: import(
            '@/features/graphics/api/generate-graphic'
          ).GenerateGraphicInput['imageryPolicy'];
          title?: string;
          fields?: Array<{ label: string; value: string }>;
        };
        if (card.serviceId && card.category) {
          return (
            <GraphicDraftPreviewCard
              id={toolPart.toolCallId}
              itemId={card.itemId}
              attemptId={card.attemptId}
              serviceId={card.serviceId}
              offerId={card.offerId}
              onRespond={onSendMessage}
              category={card.category}
              imageMode={card.imageMode}
              kind={card.kind}
              style={card.style}
              slideCount={card.slideCount}
              candidateAssetIds={card.candidateAssetIds}
              sourceAssetIds={card.sourceAssetIds}
              topicSummary={card.topicSummary}
              refinementInstruction={card.refinementInstruction}
              includeLogo={card.includeLogo}
              logoPlacement={card.logoPlacement}
              imageryPolicy={card.imageryPolicy}
              title={card.title}
              fields={card.fields}
              suppressSelectedHighlight={suppressArtifactHighlight}
              onReplyStale={onReplyStale}
            />
          );
        }
        break;
      }

      case 'graphic_status': {
        // `graphic_status` is a placeholder row already rendering; the card
        // polls and swaps to the image when the worker finishes.
        const card = envelope as {
          graphicId?: string;
          itemId?: string;
          attemptId?: string;
          status?: string;
          title?: string;
        };
        if (card.graphicId) {
          // The server already knows whether this edit is merely staged —
          // `content_patchContent` sets this on its raw output whenever a
          // change is waiting on the owner to spend a render (see
          // `PatchContentOutput.awaitingApproval`). That's the authoritative
          // signal for "nothing was actually generated by this call", not an
          // inference from comparing graphic ids across the transcript: the
          // FIRST staged edit for an item has no earlier graphic_status card
          // to compare against (the original render comes from the image
          // picker's own client-side flow, never a tool-call output), so an
          // id-comparison heuristic misses exactly that case.
          const awaitingApproval =
            (output as { awaitingApproval?: boolean } | undefined)
              ?.awaitingApproval === true;
          return (
            <GraphicStatusCard
              onSendMessage={onSendMessage}
              followLatest={
                toolName === 'content_patchContent' || awaitingApproval
              }
              previewSlideIndex={
                (input as { source?: { slideIndex?: number } } | undefined)
                  ?.source?.slideIndex
              }
              itemId={card.itemId}
              attemptId={card.attemptId}
              suppressSelectedHighlight={suppressArtifactHighlight}
              awaitingApproval={awaitingApproval}
              data={{
                graphicId: card.graphicId,
                status: card.status,
                title: card.title,
              }}
            />
          );
        }
        break;
      }

      case 'ad_preview': {
        // The rich ad card — caption, headline, CTA, creative — rather than a
        // definition list. Phase 4A: the rich creative/copy fields moved onto
        // the envelope (`presentation.preview`) so the envelope carries the
        // render payload. A 'launched' card carries `launchState` — the state
        // read back from Meta (ADR-005) — so the pill says Live / In review /
        // Campaign paused rather than what we hoped for.
        const previewRaw = (envelope as { preview?: unknown }).preview;
        if (previewRaw && typeof previewRaw === 'object') {
          const adId = (output as { adId?: unknown })?.adId;
          const preview: AdPreviewProps = {
            ...(previewRaw as AdPreviewProps),
            ...(typeof adId === 'string' ? { adId } : {}),
          };
          if (preview.variant !== 'draft' && preview.variant !== 'launched') {
            break;
          }
          // A SAVED ad is an artifact: a row in the chat, the full ad in the
          // side panel — the same shape a finished graphic or video takes.
          // Only an output with no `adId` (an old persisted message from
          // before ads were saved before being shown) still gets the inline
          // card, because there is nothing for the panel to fetch.
          const card =
            typeof adId === 'string' ? (
              <AdArtifactRow
                preview={{ ...preview, adId }}
                suppressSelectedHighlight={suppressArtifactHighlight}
              />
            ) : (
              <AdPreviewCard {...preview} onSendMessage={onSendMessage} />
            );
          const launchToken = (output as { confirmationToken?: unknown })
            ?.confirmationToken;
          if (preview.variant === 'draft' && typeof launchToken === 'string') {
            return (
              <>
                {card}
                <DraftAdLaunchDecision
                  id={toolPart.toolCallId}
                  adName={preview.adName}
                  budgetDisplay={preview.budgetDisplay ?? undefined}
                  onSendMessage={onSendMessage}
                />
              </>
            );
          }
          return card;
        }
        break;
      }

      case 'confirmation_required': {
        // Destructive confirmation, for ANY tool.
        //
        // The factory returns this envelope for every destructive tool —
        // summary, action, token — and the app used to render none of it, so
        // confirming happened in prose. Dispatching on the envelope rather
        // than the tool name is the point: the alternative is a branch per
        // tool, which is exactly how `regenerateItem` shipped with Claire
        // announcing "confirm on the card above" over a card that did not
        // exist.
        const confirmation =
          envelope as unknown as ConfirmationRequiredPresentation;
        return (
          <>
            {confirmation.summary?.campaignPackage ? (
              <CampaignPackageAdPreviews
                onSendMessage={onSendMessage}
                review={confirmation.summary.campaignPackage}
              />
            ) : null}
            <ConfirmationRequiredCard
              id={toolPart.toolCallId}
              presentation={confirmation}
              onConfirm={onSendMessage}
            />
          </>
        );
      }

      case 'support_chat': {
        const card = envelope as {
          reason?: string;
          created?: boolean;
          notConfigured?: boolean;
        };
        return (
          <SupportChatCard
            reason={card.reason ?? ''}
            created={!!card.created}
            notConfigured={card.notConfigured}
          />
        );
      }

      case 'asset_reference': {
        const card = envelope as unknown as {
          assetRef: string;
          candidates: Array<{
            id: string;
            name: string;
            thumbnailUrl?: string | null;
            blobUrl?: string | null;
          }>;
        };
        return (
          <AssetReferenceCard
            id={toolPart.toolCallId}
            assetRef={card.assetRef}
            candidates={card.candidates}
            onSendMessage={onSendMessage}
          />
        );
      }

      case 'qr_code': {
        const card = envelope as {
          recordingUrl?: string;
          instructions?: string;
        };
        if (card.recordingUrl) {
          return (
            <QRCodeCard
              recordingUrl={card.recordingUrl}
              instructions={card.instructions}
            />
          );
        }
        break;
      }

      case 'asset_picker': {
        const card = envelope as {
          total?: number;
          requiresSelection?: boolean;
          assets?: Array<{
            id: string;
            name: string;
            type?: string;
            duration?: number | string | null;
            blobUrl?: string | null;
            thumbnailUrl?: string | null;
            tags?: string[] | null;
          }>;
        };
        if (card.assets) {
          if (card.assets.length === 0) {
            return (
              <p
                data-testid="claire-card-asset-picker"
                className="italic text-muted-foreground text-xs"
              >
                No assets found.
              </p>
            );
          }
          const assets = card.assets;
          return (
            <div data-testid="claire-card-asset-picker">
              <ClipSelectionGrid
                id={toolPart.toolCallId}
                requiresSelection={card.requiresSelection ?? false}
                total={card.total}
                onCancel={() => onSendMessage('Cancel this selection.')}
                assets={assets}
                onConfirm={(ids) => {
                  onSendMessage(buildAssetPickerSelectionMessage(assets, ids));
                }}
              />
            </div>
          );
        }
        break;
      }

      case 'draft_reply': {
        // The drafted reply. Not a token confirmation — nothing is staged
        // server-side — but sending is still an outward action, so the
        // operator holds it and the outcome goes back as a message.
        const card = envelope as {
          conversationId?: string;
          draft?: string;
          customerName?: string | null;
          platform?: string;
        };
        if (card.draft && card.conversationId) {
          return (
            <DraftReplyCard
              id={toolPart.toolCallId}
              conversationId={card.conversationId}
              draft={card.draft}
              customerName={card.customerName ?? null}
              platform={card.platform}
              onRespond={onSendMessage}
            />
          );
        }
        break;
      }

      case 'campaign_preview': {
        const data = output as unknown as CampaignPreviewData;
        if (!data.campaignId) return null;
        return (
          <CampaignPreviewCard data={data} onSendMessage={onSendMessage} />
        );
      }

      case 'open_in_app': {
        // The website designer's approval card.
        const card = envelope as {
          designerRequest?: {
            locationName?: string;
            organizationId: string;
            locationId: string;
            request: string;
          };
        };
        if (card.designerRequest)
          return (
            <WebsiteDesignerApprovalCard
              id={toolPart.toolCallId}
              request={card.designerRequest}
            />
          );
        break;
      }

      case 'stop_and_ask': {
        // Circuit breaker (Phase 4 #9 #37) — factory-attached `stop_and_ask`.
        const card = envelope as { failureCount?: number; reason?: string };
        return (
          <StopAndAskCard
            label={toolNameToLabel(toolName)}
            failureCount={card.failureCount}
            reason={card.reason}
          />
        );
      }

      case 'existing_candidates': {
        // Duplicate-protection picker (Phase 4 #79 #151). The emitter tools
        // (`createCampaign`, `createLeadForm`) carry `kind`, `proposedName`
        // and `candidates` under `presentation`; Phase 3 collapsed the
        // former dual shape so this dispatch reads only the envelope.
        const card = envelope as {
          kind?: 'lead_form' | 'campaign';
          proposedName?: string;
          candidates?: ExistingCandidate[];
        };
        return (
          <ExistingCandidatesCard
            id={toolPart.toolCallId}
            kind={card.kind ?? 'campaign'}
            proposedName={card.proposedName}
            candidates={card.candidates ?? []}
            onPrompt={onSendMessage}
          />
        );
      }

      case 'preview_card': {
        // Window 7 preview cards (`show_ad_preview` / `show_offer_preview`).
        // `asPreviewCardPayload` decodes the FE-typed state from the
        // envelope payload.
        const previewPayload = asPreviewCardPayload(output);
        if (previewPayload?.kind === 'ad') {
          return (
            <ClaireAdPreviewCard
              draft={previewPayload.state}
              onSendMessage={onSendMessage}
            />
          );
        }
        if (previewPayload?.kind === 'offer') {
          return <OfferPreviewCard draft={previewPayload.state} />;
        }
        return null;
      }

      case 'memory_suggestion': {
        // Parked beside the chat (`MemoryProposalTray`), never in it.
        if ((envelope as { proposal?: unknown }).proposal) return null;
        const card = envelope as unknown as {
          content: string;
          scopeSkillIds?: string[];
          scopeLabel: string;
          conflictsWith?: { knowledgeEntryId: string; content: string };
        };
        return (
          <MemorySuggestionCard
            id={toolPart.toolCallId}
            content={card.content}
            scopeSkillIds={card.scopeSkillIds}
            scopeLabel={card.scopeLabel}
            conflictsWith={card.conflictsWith}
            answer={memorySuggestionAnswer}
            onSendMessage={onSendMessage}
          />
        );
      }

      case 'memory_saved':
        return (
          <MemorySavedCard
            presentation={envelope as unknown as MemorySavedPresentation}
          />
        );

      case 'memory_removed':
        return (
          <MemoryRemovedCard
            presentation={envelope as unknown as MemoryRemovedPresentation}
          />
        );

      case 'memory_updated':
        return (
          <MemoryUpdatedCard
            presentation={envelope as unknown as MemoryUpdatedPresentation}
          />
        );

      case 'memory_list':
        return (
          <MemoryListCard
            presentation={envelope as unknown as MemoryListPresentation}
          />
        );

      case 'confirmation_expired':
        // Used to render nothing, leaving the owner with no card to press
        // after an approval failed. See `ApprovalNotRunNotice`.
        return (
          <ApprovalNotRunNotice
            presentation={
              envelope as unknown as Extract<
                CardEnvelope,
                { type: 'confirmation_expired' }
              >
            }
            onAskAgain={(text) => onSendMessage(text)}
          />
        );

      case 'none':
      case 'tour_dispatch':
      case 'hard_block_violation':
        // Envelope kinds intentionally not rendered here — either handled
        // outside the chat surface (`tour_dispatch` fires the walkthrough
        // provider), acknowledged in the chain-of-thought trace only
        // (`hard_block_violation`), or an explicit "render nothing"
        // sentinel (`none`).
        return null;

      default: {
        // Phase 3 tripwire — every `CardEnvelope['type']` MUST have a case
        // above. Adding a new kind to `card-envelope.ts` without a matching
        // branch here stops compilation.
        const _exhaustive: never = kind;
        void _exhaustive;
        return null;
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Non-envelope dispatch. These paths handle tool results that carry NO
  // `presentation` envelope — the shape is inferred from `output`
  // (`isCreatingState` / `isCreatedState`) or from the tool name
  // (`setEnabled`). They run only when the envelope switch above had
  // nothing to render, so an envelope-emitting tool always wins.
  // ──────────────────────────────────────────────────────────────────────
  if (output && isCreatingState(output)) {
    return <CreatingStatus label={output.label} detail={output.detail} />;
  }

  if (output && isCreatedState(output)) {
    if (
      toolName === 'meta_ads_createCampaign' &&
      typeof output.campaignId === 'string'
    ) {
      return (
        <SavedCampaignCreatedCard
          campaignId={output.campaignId}
          title={output.title}
          fields={output.fields}
          variant={output.variant}
        />
      );
    }
    return (
      <CreatedCard
        title={output.title}
        fields={output.fields}
        actions={output.actions}
        variant={output.variant}
        onPrompt={onSendMessage}
      />
    );
  }

  // `chatbots_setEnabled` — the chatbot kill switch (Phase 8 / #65). Keyed
  // on the tool name rather than a card type because the tool returns plain
  // `data`, not a `presentation` card. Shows the PERSISTED flag the endpoint
  // reported, not the requested one.
  if (
    state === 'output-available' &&
    output &&
    toolName === 'chatbots_setEnabled'
  ) {
    return <ChatbotToggleCard output={output} />;
  }

  if (state === 'output-error') {
    return (
      <p className="text-destructive text-xs">
        {toolNameToLabel(toolName)} failed: Couldn't complete this action.
        Please try again.
      </p>
    );
  }

  // Anything else is folded into the chain-of-thought trace; render nothing.
  return null;
}

/** Claim advisories are persisted metadata; normal cards own their presentation. */
export function ToolRenderer(props: ToolRendererProps) {
  return <ToolResultContent {...props} />;
}
